// WebSocket client for `/v1/ephemeral` (web). Mirrors `apps/web/src/core/network/socket.ts`.

export type EphemeralConnState = 'connecting' | 'connected' | 'disconnected' | 'reconnecting';

export interface EphemeralSocketSubscription {
  unsubscribe(): void;
}

export interface EphemeralSocket {
  subscribe(
    onBinary: (data: ArrayBuffer) => void,
    onText: (data: string) => void,
    onState: (state: EphemeralConnState) => void,
  ): EphemeralSocketSubscription;
  send(data: string | ArrayBuffer | Uint8Array): void;
}

export interface CreateEphemeralSocketOptions {
  readonly url: string;
  readonly getToken: () => string | null;
  readonly channelName?: string;
  readonly onReconnect?: () => Promise<void>;
}

const LIVENESS_WINDOW_MS = 10 * 60_000;
const LIVENESS_CHECK_INTERVAL_MS = 60_000;
const INITIAL_BACKOFF_MS = 250;
const MAX_BACKOFF_MS = 30_000;
const JITTER_MS = 1_000;
const LOCK_WAIT_MS = 50;
const STABLE_CONNECTION_MS = 30_000;
const TAB_HIDDEN_CLOSE_MS = 30 * 60_000;

export function createEphemeralSocket(opts: CreateEphemeralSocketOptions): EphemeralSocket {
  const { url, getToken, channelName = 'ping-ephemeral-socket-lock', onReconnect } = opts;

  let ws: WebSocket | null = null;
  let reconnectAttempt = 0;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let livenessTimer: ReturnType<typeof setInterval> | null = null;
  let stableConnectionTimer: ReturnType<typeof setTimeout> | null = null;
  let tabHiddenTimer: ReturnType<typeof setTimeout> | null = null;
  let isLeader = false;
  let isActive = false;
  let channel: BroadcastChannel | null = null;
  let isTabVisible = typeof document === 'undefined' ? true : !document.hidden;
  let lastFrameAt = 0;
  let isOnline = typeof navigator === 'undefined' ? true : navigator.onLine;

  const binaryCallbacks: Array<(data: ArrayBuffer) => void> = [];
  const textCallbacks: Array<(data: string) => void> = [];
  const stateCallbacks: Array<(s: EphemeralConnState) => void> = [];

  function emitState(state: EphemeralConnState): void {
    for (const cb of stateCallbacks) {
      cb(state);
    }
  }

  function clearTimers(): void {
    if (reconnectTimer != null) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
    if (livenessTimer != null) {
      clearInterval(livenessTimer);
      livenessTimer = null;
    }
    if (stableConnectionTimer != null) {
      clearTimeout(stableConnectionTimer);
      stableConnectionTimer = null;
    }
    if (tabHiddenTimer != null) {
      clearTimeout(tabHiddenTimer);
      tabHiddenTimer = null;
    }
  }

  async function scheduleReconnect(): Promise<void> {
    if (!isActive || !isLeader || !isTabVisible || !isOnline) {
      emitState('disconnected');
      return;
    }
    emitState('reconnecting');
    if (onReconnect != null) {
      try {
        await onReconnect();
      } catch {
        // continue with reconnect attempt
      }
    }
    const base = Math.min(
      INITIAL_BACKOFF_MS * 2 ** reconnectAttempt,
      MAX_BACKOFF_MS,
    );
    const delay = base + Math.floor(Math.random() * JITTER_MS);
    reconnectAttempt += 1;
    reconnectTimer = setTimeout(() => {
      connect();
    }, delay);
  }

  function startLivenessWatchdog(): void {
    if (livenessTimer != null) return;
    livenessTimer = setInterval(() => {
      if (Date.now() - lastFrameAt > LIVENESS_WINDOW_MS) {
        ws?.close();
      }
    }, LIVENESS_CHECK_INTERVAL_MS);
  }

  function handleMessage(event: MessageEvent): void {
    lastFrameAt = Date.now();
    if (event.data instanceof ArrayBuffer) {
      for (const cb of binaryCallbacks) {
        cb(event.data);
      }
      return;
    }
    if (typeof event.data === 'string') {
      for (const cb of textCallbacks) {
        cb(event.data);
      }
    }
  }

  async function electLeader(): Promise<boolean> {
    if (typeof BroadcastChannel === 'undefined') {
      return true;
    }
    return new Promise((resolve) => {
      const ch = new BroadcastChannel(channelName);
      let decided = false;
      const finish = (leader: boolean) => {
        if (decided) return;
        decided = true;
        ch.close();
        resolve(leader);
      };
      ch.postMessage({ type: 'LOCK_REQUEST' });
      const timer = setTimeout(() => finish(true), LOCK_WAIT_MS);
      ch.onmessage = (ev: MessageEvent) => {
        if (ev.data?.type === 'LOCK_REQUEST') {
          ch.postMessage({ type: 'LOCK_RESPONSE' });
          clearTimeout(timer);
          finish(false);
        }
      };
    });
  }

  function setupChannelListeners(): void {
    if (channel == null) return;
    channel.addEventListener('message', (event: MessageEvent) => {
      if (event.data.type === 'LOCK_RELEASE' && !isLeader) {
        electLeader().then((leader) => {
          if (leader) {
            isLeader = true;
            connect();
          }
        });
      }
    });
  }

  function connect(): void {
    if (!isActive || !isTabVisible) return;
    if (!isLeader) {
      emitState('disconnected');
      return;
    }
    const token = getToken();
    if (token == null) {
      emitState('disconnected');
      return;
    }
    emitState('connecting');
    const separator = url.includes('?') ? '&' : '?';
    const authedUrl = `${url}${separator}token=${encodeURIComponent(token)}`;
    ws = new WebSocket(authedUrl);
    ws.binaryType = 'arraybuffer';

    ws.addEventListener('open', () => {
      lastFrameAt = Date.now();
      emitState('connected');
      startLivenessWatchdog();
      if (stableConnectionTimer != null) clearTimeout(stableConnectionTimer);
      stableConnectionTimer = setTimeout(() => {
        reconnectAttempt = 0;
      }, STABLE_CONNECTION_MS);
    });

    ws.addEventListener('message', handleMessage);
    ws.addEventListener('close', () => {
      clearTimers();
      if (isActive && isLeader && isTabVisible && isOnline) {
        void scheduleReconnect();
      } else {
        emitState('disconnected');
      }
    });
    ws.addEventListener('error', () => {
      ws?.close();
    });
  }

  function handleVisibilityChange(): void {
    if (typeof document === 'undefined') return;
    isTabVisible = !document.hidden;
    if (isTabVisible) {
      if (tabHiddenTimer != null) {
        clearTimeout(tabHiddenTimer);
        tabHiddenTimer = null;
      }
      if (isActive && isLeader && ws?.readyState !== WebSocket.OPEN) {
        reconnectAttempt = 0;
        if (onReconnect != null) {
          onReconnect()
            .then(() => connect())
            .catch(() => connect());
        } else {
          connect();
        }
      }
    } else if (isActive && isLeader && ws?.readyState === WebSocket.OPEN) {
      tabHiddenTimer = setTimeout(() => {
        if (!isTabVisible && ws?.readyState === WebSocket.OPEN) {
          ws.close();
        }
      }, TAB_HIDDEN_CLOSE_MS);
    }
  }

  function handleOnline(): void {
    isOnline = true;
    if (isActive && isLeader && isTabVisible && ws?.readyState !== WebSocket.OPEN) {
      reconnectAttempt = 0;
      if (reconnectTimer != null) {
        clearTimeout(reconnectTimer);
        reconnectTimer = null;
      }
      connect();
    }
  }

  function handleOffline(): void {
    isOnline = false;
  }

  function unsubscribeAll(): void {
    isActive = false;
    clearTimers();
    if (ws != null) {
      ws.close();
      ws = null;
    }
    if (isLeader && channel != null) {
      channel.postMessage({ type: 'LOCK_RELEASE' });
    }
    if (channel != null) {
      channel.close();
      channel = null;
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    }
    // RN-macOS exposes a partial `window` (typeof === 'object') with NO
    // `addEventListener`/`removeEventListener`, so guard on the METHOD.
    if (typeof window !== 'undefined' && typeof window.removeEventListener === 'function') {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    }
  }

  function subscribe(
    onBinary: (data: ArrayBuffer) => void,
    onText: (data: string) => void,
    onState: (state: EphemeralConnState) => void,
  ): EphemeralSocketSubscription {
    binaryCallbacks.push(onBinary);
    textCallbacks.push(onText);
    stateCallbacks.push(onState);

    if (!isActive) {
      isActive = true;
      if (typeof document !== 'undefined') {
        document.addEventListener('visibilitychange', handleVisibilityChange);
      }
      if (typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
        window.addEventListener('online', handleOnline);
        window.addEventListener('offline', handleOffline);
      }
      if (typeof BroadcastChannel !== 'undefined') {
        channel = new BroadcastChannel(channelName);
      }
      electLeader().then((leader) => {
        isLeader = leader;
        setupChannelListeners();
        if (isLeader && isTabVisible) {
          connect();
        } else {
          emitState('disconnected');
        }
      });
    }

    return {
      unsubscribe: () => {
        const bi = binaryCallbacks.indexOf(onBinary);
        if (bi >= 0) binaryCallbacks.splice(bi, 1);
        const ti = textCallbacks.indexOf(onText);
        if (ti >= 0) textCallbacks.splice(ti, 1);
        const si = stateCallbacks.indexOf(onState);
        if (si >= 0) stateCallbacks.splice(si, 1);
        if (
          binaryCallbacks.length === 0 &&
          textCallbacks.length === 0 &&
          stateCallbacks.length === 0
        ) {
          unsubscribeAll();
        }
      },
    };
  }

  function send(data: string | ArrayBuffer | Uint8Array): void {
    if (ws?.readyState !== WebSocket.OPEN) return;
    if (data instanceof Uint8Array) {
      ws.send(data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength));
      return;
    }
    ws.send(data);
  }

  return { subscribe, send };
}
